import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { t } from "@lingui/macro";
import { CopySimple, DotsSixVertical, DotsThreeVertical, PencilSimple, TrashSimple } from "@phosphor-icons/react";
import {
  Button,
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { motion } from "framer-motion";
const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const toRenderableHtml = (value?: string): string => {
  if (!value) return "";

  const trimmed = value.trim();
  if (!trimmed) return "";

  // Keep existing rich text from editor/importers untouched.
  if (/<[a-z][\s\S]*>/i.test(trimmed)) return trimmed;

  const lines = trimmed
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) return "";

  const bulletLines = lines
    .map((line) => line.replace(/^[-*•]\s*/, "").trim())
    .filter(Boolean);
  const hasBulletPrefix = lines.some((line) => /^[-*•]\s+/.test(line));

  if (hasBulletPrefix || lines.length > 1) {
    return `<ul>${bulletLines.map((line) => `<li>${escapeHtml(line)}</li>`).join("")}</ul>`;
  }

  return `<p>${escapeHtml(lines[0])}</p>`;
};

export type SectionListItemProps = {
  id: string;
  title: string;
  isMastersUnion?: boolean;
  visible?: boolean;
  description?: string;
  summary?: string;

  // Callbacks
  onUpdate?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onToggleVisibility?: () => void;
};

export const SectionListItem = ({
  id,
  title,
  isMastersUnion = false,
  description,
  summary,
  visible = true,
  onUpdate,
  onDuplicate,
  onDelete,
  onToggleVisibility,
}: SectionListItemProps) => {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
    id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : undefined,
    zIndex: isDragging ? 100 : undefined,
    transition,
  };
  const safeTitle = title.trim();
  const safeDescription = description?.trim();
  const safeSummary = toRenderableHtml(summary);
  const showSummaryFallback = !safeTitle && !safeDescription && Boolean(safeSummary);

  return (
    <motion.section
      ref={setNodeRef}
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="border-x border-t bg-secondary/10 first-of-type:rounded-t last-of-type:rounded-b last-of-type:border-b"
    >
      <div style={style} className="flex transition-opacity">
        {/* Drag Handle */}
        <div
          {...listeners}
          {...attributes}
          className={cn(
            "flex w-5 cursor-move items-center justify-center",
            !isDragging && "hover:bg-secondary",
          )}
        >
          <DotsSixVertical weight="bold" size={12} />
        </div>

        {/* List Item */}
        {isMastersUnion ? (
          <>
            {/* <ContextMenu>
              <ContextMenuTrigger asChild> */}
            <div
              onClick={onUpdate}
              className={cn("flex-1 p-4 hover:bg-secondary-accent", !visible && "opacity-50")}
            >
              {safeTitle && <h4 className="font-medium leading-relaxed">{safeTitle}</h4>}
              {safeDescription && <p className="text-xs leading-relaxed opacity-50">{safeDescription}</p>}
              {showSummaryFallback && (
                <div
                  className="text-xs leading-relaxed opacity-50 [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:my-0 [&_li]:my-0"
                  dangerouslySetInnerHTML={{ __html: safeSummary }}
                />
              )}
            </div>
            {/* </ContextMenuTrigger>
              <ContextMenuContent>
                <ContextMenuCheckboxItem checked={visible} onCheckedChange={onToggleVisibility}>
                  <span className="-ml-0.5">{t`Visible`}</span>
                </ContextMenuCheckboxItem>
                <ContextMenuItem onClick={onUpdate}>
                  <PencilSimple size={14} />
                  <span className="ml-2">{t`Edit`}</span>
                </ContextMenuItem>
                <ContextMenuItem onClick={onDuplicate}>
                  <CopySimple size={14} />
                  <span className="ml-2">{t`Copy`}</span>
                </ContextMenuItem>
                <ContextMenuItem className="text-error" onClick={onDelete}>
                  <TrashSimple size={14} />
                  <span className="ml-2">{t`Remove`}</span>
                </ContextMenuItem>
              </ContextMenuContent>
            </ContextMenu> */}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="mr-2 mt-2 shrink-0 self-start"
                >
                  <DotsThreeVertical size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuCheckboxItem checked={visible} onCheckedChange={onToggleVisibility}>
                  <span className="-ml-0.5">{t`Visible`}</span>
                </DropdownMenuCheckboxItem>
                <DropdownMenuItem onClick={onUpdate}>
                  <PencilSimple size={14} />
                  <span className="ml-2">{t`Edit`}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onDuplicate}>
                  <CopySimple size={14} />
                  <span className="ml-2">{t`Copy`}</span>
                </DropdownMenuItem>
                <DropdownMenuItem className="text-error" onClick={onDelete}>
                  <TrashSimple size={14} />
                  <span className="ml-2">{t`Remove`}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : (
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <div
              onClick={onUpdate}
              className={cn(
                "flex-1 cursor-context-menu p-4 hover:bg-secondary-accent",
                !visible && "opacity-50",
              )}
            >
              {safeTitle && <h4 className="font-medium leading-relaxed">{safeTitle}</h4>}
              {safeDescription && <p className="text-xs leading-relaxed opacity-50">{safeDescription}</p>}
              {showSummaryFallback && (
                <div
                  className="text-xs leading-relaxed opacity-50 [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:my-0 [&_li]:my-0"
                  dangerouslySetInnerHTML={{ __html: safeSummary }}
                />
              )}
            </div>
          </ContextMenuTrigger>
          <ContextMenuContent>
            <ContextMenuCheckboxItem checked={visible} onCheckedChange={onToggleVisibility}>
              <span className="-ml-0.5">{t`Visible`}</span>
            </ContextMenuCheckboxItem>
            <ContextMenuItem onClick={onUpdate}>
              <PencilSimple size={14} />
              <span className="ml-2">{t`Edit`}</span>
            </ContextMenuItem>
            <ContextMenuItem onClick={onDuplicate}>
              <CopySimple size={14} />
              <span className="ml-2">{t`Copy`}</span>
            </ContextMenuItem>
            <ContextMenuItem className="text-error" onClick={onDelete}>
              <TrashSimple size={14} />
              <span className="ml-2">{t`Remove`}</span>
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
        )}
      </div>
    </motion.section>
  );
};
